import React from 'react'

import moment from 'moment'
import _ from 'lodash'

import {emit} from 'src/socketio/socketEmit.js'

import dbCodes from '../../../server/dbCodes.js'
import ParcelActions from '../../actions/ParcelActions.js'
import NewParcel from '../../../server/functions/parcels/NewParcel.js'
import {isIntrapostEnvelope} from '../../../server/functions/parcels/trackTrace.js'
import countries from '../../utils/Countries.js'

import {Alert, Button, Colors, IconButton, Link, Modal, P, ScrollView, S2, H4, Card, Row, Spinner, S1} from '../UI/index.js'
import CarrierLogo from './CarrierLogo.js'
import {
    CurrencyInput,
    FormRow,
    FormStack,
    Input,
    MetricInput,
    Select,
    TextArea,
    formSpacing
} from '../UI/forms/index.js'
import Info from './Info.js'
import SendTrackTracePopup from './SendTrackTracePopup.js'
import DividerHeader from '../UI/surfaces/DividerHeader.js'
import {getCarrierLabel} from './CarrierLogo.js'
import {Reseller} from '../../../server/functions/resellers/resellers.types.js'
import {User} from '../../../server/functions/users/users.types.js'
import {ParcelCarrier} from '../../../server/functions/parcels/carrier.types.js'
import {Customer} from '../../../server/functions/customers/customers.types.js'
import {Parcel} from '../../../server/functions/parcels/parcels.types.js'
import CustomerSupportModal, {CustomerSupportModalHandle} from './CustomerSupportModal.js'
import Languages from '@utils/Languages.js'
import {formatPrice, parsePrice} from '../../../utils/price.js'
import {getCustomsCurrency, getCurrencySymbol} from '../../../utils/customsCurrencies.js'
import t from '../../helpers/Languages.js'

// Keeps line breaks typed in the return portal and wraps long words/URLs inside the field
const readOnlyTextStyle: React.CSSProperties = {whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}

interface ViewParcelModalProps {
    reseller: Reseller
    user: (Customer | User) & {isCustomer?: boolean}
    history: any
    match: any
    carriers: ParcelCarrier[]
    customers: {
        [key: string]: Customer
    }
    onClickCopy: (parcelId: string, multiple?: boolean, options?: string[]) => void
    onClickPrintLabels: (parcelId: string) => void
}

interface ViewParcelModalState {
    modalIsOpen: boolean
    parcel: Parcel
    customer: string
    price: string
    fees: Parcel['fees']
    priceLocked: boolean
    emailTT: string
    weight: number
    length: number
    width: number
    height: number
    editCustomer: boolean
    editDimensionsAndWeight: boolean
    editAdministration: boolean
    notes: string
    loading: boolean
    parcelLoading?: boolean
    loadingStatus?: boolean
    message: string
    error: string
    parcelError?: string | null
}

export default class ViewParcelModal extends React.Component<ViewParcelModalProps, ViewParcelModalState> {
    initialState: ViewParcelModalState
    sendTrackTracePopup: SendTrackTracePopup | null
    customerSupportModal: CustomerSupportModalHandle

    constructor(props) {
        super(props)

        this.initialState = {
            modalIsOpen: false,
            parcel: NewParcel(),
            customer: '',
            price: '',
            fees: [],
            priceLocked: false,
            emailTT: '',
            weight: 0,
            length: 0,
            width: 0,
            height: 0,
            editCustomer: false,
            editDimensionsAndWeight: false,
            editAdministration: false,
            notes: '',
            loading: false,
            message: '',
            error: ''
        }

        this.state = JSON.parse(JSON.stringify(this.initialState))
    }

    open(id) {
        this.setState({modalIsOpen: true, parcelLoading: true})

        ParcelActions.getOne(id, (err, parcel) => {
            if (err) {
                this.setState({parcelLoading: false, parcelError: err})
            } else {
                console.log(parcel)

                const {reseller, user} = this.props
                parcel = parcel ? JSON.parse(JSON.stringify(parcel)) : NewParcel()

                if (!parcel._id && user.isCustomer) {
                    parcel.customer = user._id
                }

                this.setState({
                    parcelLoading: false,
                    parcelError: null,
                    parcel,
                    customer: parcel.customer || '',
                    price: parcel.price,
                    fees: JSON.parse(JSON.stringify(parcel.fees || [])),
                    priceLocked: !!parcel.priceLocked,
                    emailTT: (user.isCustomer || reseller.isShop) ? parcel.address.email : parcel.emailTT || '',
                    weight: parcel.weight || 0,
                    length: parcel.length || 0,
                    width: parcel.width || 0,
                    height: parcel.height || 0,
                    editCustomer: false,
                    editDimensionsAndWeight: false,
                    editAdministration: false,
                    notes: parcel.notes
                })
            }
        })
    }

    close() {
        const {user, history} = this.props
        const {parcel, notes} = this.state

        if (notes !== parcel.notes) {
            ParcelActions.editNotes(parcel._id, notes)
        }

        this.setState(this.initialState)

        if (this.props.match?.params?.parcelId) {
            if (user.isCustomer) {
                history.push('/klant/zendingen')
            } else {
                history.push('/zendingen')
            }
        }
    }

    onClickGotoTrackTrace(event) {
        event.preventDefault()
        const parcel = {...this.state.parcel}

        if ((parcel.trackTraceLink || '').indexOf('veloyd') > -1) {
            window.open(`/track&trace/${parcel.trackTrace}`)
        } else {
            window.open(parcel.trackTraceLink)
        }
    }

    onSendTrackTraceEmail() {
        const parcel = {...this.state.parcel}
        const {emailTT} = this.state

        this.setState({message: '', error: ''})

        this.sendTrackTracePopup?.open(parcel, emailTT, this.response.bind(this))
    }

    onClickRetour() {
        const parcel = {...this.state.parcel}
        emit('parcel.retour', parcel, (err, updatedParcel) => {
            if (err) {
                this.setState({error: err})
            } else {
                this.setState({parcel: updatedParcel})
            }
        })
    }

    onChangeEditCustomer(event) {
        event.preventDefault()
        const {editCustomer} = this.state
        this.setState({editCustomer: !editCustomer})
    }

    onChangeCustomer({customer}) {
        if (customer) {
            this.setState({customer: customer._id})
        }
    }

    onSaveCustomer(event) {
        event.preventDefault()
        const {customer} = this.state
        const parcel = {...this.state.parcel}

        if (parcel.customer !== customer) {
            parcel.customer = customer
            parcel.contact = ''
            ParcelActions.editCustomer(parcel._id, parcel.customer, parcel.contact, (err, updatedParcel) => {
                if (err) {
                    this.setState({error: err})
                } else {
                    this.setState({parcel: updatedParcel})
                }
            })
        }

        this.setState({editCustomer: false})
    }

    onChangeWeight(event) {
        if (/^[0-9]{0,5}$/.test(event.target.value)) {
            const weight = parseInt(event.target.value) || 0
            this.setState({weight})
        }
    }

    onChangeDimension(key: 'length' | 'width' | 'height', event) {
        if (key === 'length') {
            this.setState({length: +event.target.value})
        }

        if (key === 'width') {
            this.setState({width: +event.target.value})
        }

        if (key === 'height') {
            this.setState({height: +event.target.value})
        }
    }

    onSaveDimensionsAndWeight(event) {
        event.preventDefault()
        const {weight, length, height, width} = this.state
        const parcel = {...this.state.parcel}

        if (parcel.weight !== weight || parcel.length !== length || parcel.width !== width || parcel.height !== height) {
            parcel.weight = weight
            parcel.length = length
            parcel.width = width
            parcel.height = height
            const dimensions = {
                length,
                width,
                height
            }
            ParcelActions.editDimensionsAndWeight(parcel._id, weight, dimensions, (err, updatedParcel: Parcel) => {
                this.setState({
                    price: updatedParcel.price,
                    parcel: updatedParcel,
                    fees: JSON.parse(JSON.stringify(updatedParcel.fees || []))

                })
            })
        }

        this.setState({editDimensionsAndWeight: false})
    }

    onChangePrice(event) {
        if (/^$|^[-]{0,1}[0-9]{0,3}$|^[-]{0,1}[0-9]{0,3}[,][0-9]{0,2}$/.test(event.target.value)) {
            this.setState({price: event.target.value})
        }
    }

    onChangeFee(key, index: number, event) {
        event.preventDefault()
        const {fees} = this.state
        const {reseller} = this.props

        if (key === 'quantity') {
            fees[index].quantity = parseInt(event.target.value) || 0
        } else {
            const selectedFee = _.find(reseller.settings.parcels.fees, {id: event.target.value})

            if (selectedFee && selectedFee.id) {
                const fee = JSON.parse(JSON.stringify(selectedFee))
                fee.quantity = 1

                if (fee.basedOn && fee.basedOn !== 'fixedPrice') {
                    fee.percentage = fee.price
                }

                fees[index] = fee
            }
        }

        this.setState({fees})
    }

    onClickAddFee(event) {
        event.preventDefault()
        const {reseller} = this.props
        const {fees} = this.state

        if (reseller.settings.parcels.fees) {
            const fee = JSON.parse(JSON.stringify(reseller.settings.parcels.fees[0]))

            if (fee && fee.description) {
                fees.push({
                    id: fee.id,
                    description: fee.description,
                    price: fee.price,
                    percentage: fee.basedOn && fee.basedOn !== 'fixedPrice' ? fee.price : undefined,
                    basedOn: fee.basedOn,
                    productCode: fee.productCode,
                    quantity: 1
                })

                this.setState({fees})
            }
        }
    }

    onClickRemoveFee(index, event) {
        event.preventDefault()
        const {fees} = this.state
        fees.splice(index, 1)
        this.setState({fees})
    }

    onSaveAdministration() {
        const {fees, priceLocked, notes} = this.state
        const parcel = {...this.state.parcel}

        const price = this.state.price === 'priceScale' ? this.state.price : formatPrice(this.state.price)

        ParcelActions.editAdministration(parcel._id, price, fees, priceLocked, notes, (err, updatedParcel) => {
            this.setState({
                parcel: updatedParcel,
                price: updatedParcel.price,
                priceLocked: updatedParcel.priceLocked,
                fees: JSON.parse(JSON.stringify(updatedParcel.fees || [])),
                notes: updatedParcel.notes
            })
        })

        this.setState({editAdministration: false, price})
    }

    onGetStatus() {
        const parcel = {...this.state.parcel}

        this.setState({loadingStatus: true})

        ParcelActions.getStatus(parcel._id, (err, updatedParcel) => {
            this.setState({loadingStatus: false, error: err})
            if (!err && updatedParcel) {
                this.setState({
                    parcel: updatedParcel,
                    fees: JSON.parse(JSON.stringify(updatedParcel.fees || []))

                })
            }
        })
    }

    response(err, response) {
        if (err) {
            this.setState({loading: false, message: '', error: err})
        } else {
            this.setState({loading: false, message: response, error: ''})
        }
    }

    render() {
        const {modalIsOpen, parcelLoading, parcelError, parcel, emailTT, notes, loadingStatus, message, error, editDimensionsAndWeight, editAdministration} = this.state
        const {reseller, history, user, carriers, customers, onClickCopy, onClickPrintLabels} = this.props

        const customer = customers?.[parcel.customer] || user
        const sizes = 'parcels' in customer.settings ? customer.settings?.parcels?.sizes || [] : []
        const sizeIndex = sizes.findIndex((size) => size.width === parcel.width && size.length === parcel.length && size.height === parcel.height)

        const removedFees = parcel.fees.filter((fee) => !reseller.settings.parcels.fees?.find((resellerFee) => resellerFee.id === fee.id))

        return (
            <Modal show={modalIsOpen} onClose={this.close.bind(this)}>
                <Row style={{alignItems: 'center', marginBottom: 12, position: 'relative'}}>
                    <Row style={{flex: 1, paddingLeft: 6, paddingRight: 6, minWidth: 'auto'}}>
                        <H4>Zending info</H4>

                        <div style={{flex: 1}} />
                    </Row>

                    {!parcelLoading &&
                        <div style={{display: 'flex', alignItems: 'center'}}>
                            <IconButton
                                tooltip='Kopieer zending'
                                onClick={() => {
                                    this.close()
                                    onClickCopy(parcel._id)
                                }}
                                icon='mdi mdi-content-copy'
                            />

                            {!parcel.options.includes('Retour') &&
                                <IconButton
                                    tooltip='Retourneer zending'
                                    onClick={() => {
                                        this.close()
                                        onClickCopy(parcel._id, false, ['Retour'])
                                    }}
                                    icon='mdi mdi-keyboard-return'
                                />
                            }

                            <IconButton
                                tooltip='Print labels'
                                onClick={() => onClickPrintLabels(parcel._id)}
                                icon='mdi mdi-printer'
                            />

                            <IconButton
                                tooltip={emailTT ? `Verstuur Track & Trace email naar: ${emailTT}` : 'Verstuur Track & Trace email'}
                                onClick={this.onSendTrackTraceEmail.bind(this)}
                                disabled={isIntrapostEnvelope(parcel)}
                                icon='mdi mdi-at'
                            />

                            {user.isCustomer && reseller.settings.parcels?.customerSupport?.enabled && reseller.settings.parcels?.customerSupport?.email &&
                                <IconButton
                                    tooltip={`${Languages.customerSupportTooltip['NL']} ${reseller.settings.accountName}`}
                                    onClick={() => this.customerSupportModal?.open(parcel)}
                                    icon='mdi mdi-alert'
                                />
                            }
                        </div>
                    }

                    <Row style={{flex: 1, paddingLeft: 6, paddingRight: 6, minWidth: 'auto'}}>
                        <div style={{flex: 1}} />

                        <IconButton onClick={this.close.bind(this)}>
                            <i style={{color: Colors.buttonSolid}} className='mdi mdi-close' />
                        </IconButton>
                    </Row>
                </Row>

                {parcelLoading ?
                    <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: 250}}>
                        <Spinner color={Colors.textDark} />
                    </div> :
                    <>
                        {!user.isCustomer &&
                            <Card style={{marginBottom: 24}}>
                                <Info
                                    customers={customers}
                                    parcel={parcel}
                                    onChangeCustomer={this.onChangeCustomer.bind(this)}
                                    onSaveCustomer={this.onSaveCustomer.bind(this)}
                                    onUpdateParcel={(parcel) => this.setState({parcel})}
                                    user={user}
                                />
                            </Card>
                        }

                        <DividerHeader style={{marginBottom: 18}} title={parcel.options.includes('Retour') ? 'Afzender' : 'Bezorgadres'}>

                            <Card style={{padding: 0}}>
                                <Row style={{height: '100%'}}>
                                    <Row style={{flex: 1, paddingLeft: 6, paddingRight: 6, flexDirection: 'column', borderRight: `1px dashed ${Colors.grey40}`, padding: 6, minWidth: 'auto'}}>
                                        <Row>
                                            <i style={{marginRight: 6, fontSize: 18}} className='mdi mdi-account-outline' />
                                            <P>{parcel.address.name}</P>
                                        </Row>

                                        <div style={{marginLeft: 24}}>
                                            <P>{parcel.address.attention}</P>
                                            <P>{`${parcel.address.street} ${parcel.address.nr}${parcel.address.addition}`}</P>
                                            <P>{parcel.address.street2}</P>
                                            <P>{`${parcel.address.postalCode} ${parcel.address.city}${parcel.address.state ? ` ${parcel.address.state}` : ''}`}</P>
                                            <P>{_.find(countries, {code2: parcel.address.country})?.name}</P>
                                        </div>

                                        {parcel.address.email &&
                                            <Row style={{marginTop: 12}}>
                                                <i style={{marginRight: 6, fontSize: 18}} className='mdi mdi-at' />
                                                <P>{parcel.address.email}</P>
                                            </Row>
                                        }

                                        {parcel.address.phone &&
                                            <Row>
                                                <i style={{marginRight: 6, fontSize: 18}} className='mdi mdi-phone' />
                                                <P>{parcel.address.phone}</P>
                                            </Row>
                                        }

                                        {parcel.comment &&
                                            <Row style={{marginTop: 12}}>
                                                <i style={{marginRight: 6, fontSize: 18}} className='mdi mdi-message-outline' />
                                                <P>{parcel.comment}</P>
                                            </Row>
                                        }
                                    </Row>

                                    <Row style={{flex: 1, paddingLeft: 6, paddingRight: 6, flexDirection: 'column', alignItems: 'center', padding: 6, minWidth: 'auto'}}>
                                        <CarrierLogo
                                            size={100}
                                            carriers={carriers}
                                            value={parcel.carrier}
                                            options={parcel.options}
                                        />

                                        <P
                                            style={{
                                                textAlign: 'center'
                                            }}
                                        >
                                            {(() => {
                                                const carrier = carriers.find((carrier) => carrier.name === parcel.carrier)

                                                return carrier ? getCarrierLabel(carrier, parcel.options).displayName : parcel.carrier
                                            })()}
                                        </P>

                                        <div style={{width: 'fit-content', marginTop: 24}}>
                                            {isIntrapostEnvelope(parcel) ?
                                                <P>Geen Track & Trace</P> :
                                                <Row>
                                                    <i style={{marginRight: 6, fontSize: 18}} className='mdi mdi-web' />
                                                    <Link onClick={this.onClickGotoTrackTrace.bind(this)}>{parcel.trackTrace}</Link>
                                                </Row>
                                            }

                                            {['GLS', 'Skynet'].includes(parcel.carrier) &&
                                                <Row>
                                                    <i style={{marginRight: 6, fontSize: 18}} className='mdi mdi-identifier' />
                                                    <P>{parcel.externId}</P>
                                                </Row>
                                            }
                                        </div>

                                    </Row>
                                </Row>
                            </Card>
                        </DividerHeader>

                        {parcel.pickupAddress &&
                            <DividerHeader style={{marginBottom: 18}} title='Ophaaladres'>

                                <Row>
                                    <i style={{marginRight: 6, fontSize: 18}} className='mdi mdi-account-outline' />
                                    <P>{parcel.pickupAddress.name}</P>
                                </Row>

                                <div style={{marginLeft: 24}}>
                                    <P>{parcel.pickupAddress.attention}</P>
                                    <P>{`${parcel.pickupAddress.street} ${parcel.pickupAddress.nr}${parcel.pickupAddress.addition}`}</P>
                                    <P>{parcel.pickupAddress.street2}</P>
                                    <P>{`${parcel.pickupAddress.postalCode} ${parcel.pickupAddress.city}${parcel.pickupAddress.state ? ` ${parcel.pickupAddress.state}` : ''}`}</P>
                                    <P>{_.find(countries, {code2: parcel.pickupAddress.country})?.name}</P>
                                </div>
                            </DividerHeader>
                        }

                        {parcel.options.includes('Afhaalpunt') &&
                            <DividerHeader style={{marginBottom: 18}} title='Afhaalpunt'>
                                <Row>
                                    <i style={{marginRight: 6, fontSize: 18}} className='mdi mdi-store-outline' />
                                    <P>{parcel.pickupPoint?.address?.name}</P>
                                </Row>

                                <div style={{marginLeft: 24}}>
                                    <P>{`${parcel.pickupPoint?.address.street} ${parcel.pickupPoint?.address.nr}${parcel.pickupPoint?.address.addition}`}</P>
                                    <P>{`${parcel.pickupPoint?.address.postalCode} ${parcel.pickupPoint?.address.city}`}</P>
                                    <P>{_.find(countries, {code2: parcel.pickupPoint?.address.country})?.name}</P>
                                </div>
                            </DividerHeader>
                        }

                        <DividerHeader
                            style={{marginBottom: 18}}
                            title='Zending details'
                            edit={editDimensionsAndWeight}
                            onClickEdit={!user.isCustomer && (() => this.setState({editDimensionsAndWeight: !editDimensionsAndWeight}))}
                            onClickSave={this.onSaveDimensionsAndWeight.bind(this)}
                        >
                            <FormRow style={{marginBottom: formSpacing.rowGap}}>
                                <FormStack style={{flex: 1}}>
                                    <FormRow>
                                        <MetricInput
                                            label='Gewicht'
                                            max={999999999}
                                            divisionFactor={1000}
                                            value={this.state.weight}
                                            onChange={this.onChangeWeight.bind(this)}
                                            readOnly={!editDimensionsAndWeight}
                                            append='kg'
                                        />

                                        <MetricInput
                                            label='Lengte'
                                            value={this.state.length}
                                            onChange={this.onChangeDimension.bind(this, 'length')}
                                            readOnly={!editDimensionsAndWeight}
                                            divisionFactor={10}
                                            append='cm'
                                        />
                                    </FormRow>

                                    <FormRow>
                                        <MetricInput
                                            label='Breedte'
                                            value={this.state.width}
                                            onChange={this.onChangeDimension.bind(this, 'width')}
                                            readOnly={!editDimensionsAndWeight}
                                            divisionFactor={10}
                                            append='cm'
                                        />

                                        <MetricInput
                                            label='Hoogte'
                                            value={this.state.height}
                                            onChange={this.onChangeDimension.bind(this, 'height')}
                                            readOnly={!editDimensionsAndWeight}
                                            divisionFactor={10}
                                            append='cm'
                                        />
                                    </FormRow>
                                </FormStack>

                                <FormStack style={{flex: 1}}>
                                    {user.isCustomer && sizeIndex > -1 && (
                                        <Select
                                            label='Standaardformaat'
                                            value={`${sizeIndex}`}
                                            options={(() => {
                                                const customer = customers?.[parcel.customer] || user
                                                const sizes = 'parcels' in customer.settings ? customer.settings.parcels?.sizes || [] : []

                                                return sizes.map((size, index) => ({
                                                    title: size.name,
                                                    value: `${index}`
                                                }))
                                            })()}
                                            readOnly
                                        />
                                    )}

                                    <TextArea
                                        label='Verzendopties'
                                        value={parcel.options.map((option) => {
                                            if (option === 'Verzekerd') {
                                                option = `Verzekerd: € ${parcel.cover}`
                                            }

                                            if (option === 'Spoed levering') {
                                                option = `Spoed levering voor ${parcel.deliveryTime} uur`
                                            }

                                            if (option === 'Pick & Ship' || option === 'Pick & Return') {
                                                option = `${option}: ${moment(parcel.pickupDate).format('DD-MM-YYYY')}`
                                            }

                                            if (user.isCustomer && option === 'Verificatiecode') {
                                                option = `Verificatiecode: ${parcel.verificationCode}`
                                            }

                                            return option
                                        }).join(', ')}
                                        readOnly
                                    />
                                </FormStack>
                            </FormRow>
                        </DividerHeader>

                        <DividerHeader
                            style={{marginBottom: 18}}
                            title='Administratie'
                            defaultHidden={true}
                            edit={editAdministration}
                            onClickEdit={!user.isCustomer && (() => this.setState({editAdministration: !editAdministration}))}
                            onClickSave={() => this.onSaveAdministration()}
                        >
                            <FormStack>
                                <FormRow>
                                    <FormRow style={{flex: 1}}>
                                        <CurrencyInput
                                            label='Prijs'
                                            info={!user.isCustomer ? 'De prijs wordt automatisch herberekend op basis van het gewicht en de afmetingen van de zending. Als je dit niet wilt, kan je de prijs vastzetten door op het slotje te klikken.' : undefined}
                                            value={this.state.price === 'priceScale' ? parcel.priceScaleRange ? `${parcel.priceScaleRange.min} - € ${parcel.priceScaleRange.max}` : 'n.t.b.' : this.state.price}
                                            readOnly={!editAdministration}
                                            onChange={this.onChangePrice.bind(this)}
                                            append={
                                                !user.isCustomer ?
                                                    <IconButton
                                                        icon={this.state.priceLocked ? 'mdi mdi-lock' : 'mdi mdi-lock-open-outline'}
                                                        disabled={!editAdministration}
                                                        onClick={() => this.setState({priceLocked: !this.state.priceLocked})}
                                                    /> :
                                                    undefined
                                            }
                                        />

                                        <Input
                                            label='Referentie'
                                            value={parcel.reference}
                                            readOnly
                                        />
                                    </FormRow>

                                    <FormStack style={{flex: 1}}>
                                        {!user.isCustomer &&
                                            <TextArea
                                                label='Notities'
                                                value={notes}
                                                onChange={(event) => this.setState({notes: event.target.value})}
                                                readOnly={!editAdministration}
                                            />
                                        }
                                    </FormStack>
                                </FormRow>

                                {(!user.isCustomer || this.state.fees.length > 0) &&
                                    this.state.fees.map((fee, index) => {
                                        return (
                                            <FormRow key={`fee${index}`}>
                                                <FormRow style={{flex: 1, alignItems: 'center'}}>
                                                    <Select
                                                        label='Toeslag'
                                                        options={[...(reseller.settings.parcels.fees || []).map((fee) => {
                                                            if (fee.id) {
                                                                return {title: fee.description, value: fee.id}
                                                            }
                                                        }), ...removedFees.map((fee) => {
                                                            if (fee.id) {
                                                                return {title: fee.description, value: fee.id, disabled: true}
                                                            }
                                                        }
                                                        )]}
                                                        value={fee.id}
                                                        readOnly={!editAdministration}
                                                        onChange={this.onChangeFee.bind(this, 'id', index)}
                                                    />

                                                    <Input
                                                        noPlaceholder
                                                        style={{maxWidth: 80}}
                                                        label='Aantal'
                                                        type='number'
                                                        value={fee.quantity}
                                                        readOnly={!editAdministration}
                                                        onChange={this.onChangeFee.bind(this, 'quantity', index)}
                                                    />

                                                    {editAdministration &&
                                                        <IconButton onClick={this.onClickRemoveFee.bind(this, index)}>
                                                            <i className='mdi mdi-delete' />
                                                        </IconButton>
                                                    }
                                                </FormRow>
                                                <div style={{flex: 1}} />
                                            </FormRow>
                                        )
                                    })
                                }

                                {editAdministration &&
                                    <Button
                                        label='Nieuwe toeslag'
                                        variant='outline-white'
                                        icon='mdi mdi-plus'
                                        onClick={this.onClickAddFee.bind(this)}
                                    />
                                }

                                {!editAdministration && (parcel.price !== '0,00' || (parcel.priceExplanation?.fees?.length || 0) > 0) && parcel.priceExplanation &&
                                    <Card>
                                        <S1 style={{marginBottom: 12}}>Prijsopbouw</S1>

                                        {parcel.priceExplanation.product && (typeof parcel.priceExplanation.product?.price !== 'string' || parcel.priceExplanation.product.price !== '0,00') &&
                                            <Row style={{width: '50%'}}>
                                                <P style={{flex: 1}}>{parcel.priceExplanation.product?.name}</P>
                                                <P>€ {typeof parcel.priceExplanation.product?.price === 'string' ? parcel.priceExplanation.product.price : `${parcel.priceExplanation.product?.price.min} - € ${parcel.priceExplanation.product?.price.max}`}</P>
                                            </Row>
                                        }

                                        {parcel.priceExplanation.options?.length > 0 &&
                                            <S2 style={{marginTop: 12}}>Opties</S2>
                                        }

                                        {parcel.priceExplanation.options?.map((option, index) => {
                                            if (typeof option.price !== 'string' || option.price !== '0,00') {
                                                return (
                                                    <Row key={`priceExplanation-fee-${index}`} style={{width: '50%'}}>
                                                        <P style={{flex: 1}}>{option.name}</P>
                                                        <P>€ {typeof option.price === 'string' ? option.price : `${option.price.min} - € ${option.price.max}`}</P>
                                                    </Row>
                                                )
                                            }
                                        })}

                                        {parcel.priceExplanation.fees?.length > 0 &&
                                            <S2 style={{marginTop: 12}}>Toeslagen</S2>
                                        }

                                        {parcel.priceExplanation.fees?.map((fee, index) => {
                                            if (typeof fee.price !== 'string' || fee.price !== '0,00') {
                                                return (
                                                    <Row key={`priceExplanation-fee-${index}`} style={{width: '50%'}}>
                                                        <P style={{flex: 1}}>{fee.name}</P>
                                                        <P>€ {typeof fee.price === 'string' ? fee.price : `${fee.price.min} - € ${fee.price.max}`}</P>
                                                    </Row>
                                                )
                                            }
                                        })}
                                    </Card>
                                }
                            </FormStack>

                        </DividerHeader>

                        {(!!parcel.customs?.content?.length || parcel.refundMethod) &&
                            <DividerHeader
                                style={{marginBottom: 18}}
                                title='Artikelen'
                                defaultHidden={true}
                            >

                                {parcel.refundMethod &&
                                    <FormRow style={{marginBottom: formSpacing.rowGap}}>
                                        <Input
                                            label='Terugbetaling'
                                            value={parcel.refundMethod === 'refund' ?
                                                'Geld terug' :
                                                parcel.refundMethod === 'exchange' ?
                                                    'Artikelen ruilen' :
                                                    'Winkel tegoed'
                                            }
                                            readOnly
                                        />

                                        {parcel.refundMethod === 'exchange' &&
                                            <TextArea
                                                inputStyle={readOnlyTextStyle}
                                                label='Terugbetaling opmerking'
                                                value={parcel.refundMethodComment}
                                                readOnly
                                            />
                                        }
                                    </FormRow>
                                }

                                {parcel.customs?.typeItem &&
                                    <FormRow style={{marginBottom: formSpacing.rowGap + formSpacing.tight}}>
                                        <Select
                                            label='Type zending'
                                            value={parcel.customs.typeItem}
                                            readOnly
                                        >
                                            <option value='Commercial Goods'>Verkoop goederen</option>
                                            <option value='Returned Goods'>Retour goederen</option>
                                            <option value='Commercial Sample'>Handelsmonster</option>
                                            <option value='Documents'>Documenten</option>
                                            <option value='Gift'>Geschenk</option>
                                        </Select>

                                        <FormRow style={{flex: 1}}>
                                            <Select
                                                label='Type nummer'
                                                value={parcel.customs.typeNumber}
                                                readOnly
                                                allowEmpty
                                            >
                                                <option value='invoice'>Factuur</option>
                                                <option value='certificate'>Certificaat</option>
                                                <option value='license'>Licentie</option>
                                            </Select>

                                            <Input
                                                label='Nummer'
                                                value={parcel.customs.number}
                                                readOnly
                                            />

                                            <Input
                                                label={t('customsCurrency')}
                                                value={getCustomsCurrency(parcel.customs)}
                                                readOnly
                                            />
                                        </FormRow>
                                    </FormRow>
                                }

                                <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                                    {parcel.address.country === 'NL' ?
                                        (parcel.content?.length ? parcel.content : parcel.customs?.content)?.map((content, index) => {
                                            return (
                                                <Card key={`content${index}`} style={{background: Colors.grey20, padding: formSpacing.inset}}>
                                                    <FormRow>
                                                        <FormRow style={{flex: 1}}>
                                                            <Input
                                                                style={{maxWidth: 60}}
                                                                label='Aantal'
                                                                value={content.quantity}
                                                                readOnly
                                                            />

                                                            <FormStack style={{flex: 1}}>
                                                                <TextArea
                                                                    inputStyle={readOnlyTextStyle}
                                                                    label='Omschrijving'
                                                                    value={content.description}
                                                                    readOnly
                                                                />
                                                                {content.comment &&
                                                                    <TextArea
                                                                        inputStyle={readOnlyTextStyle}
                                                                        label='Reden'
                                                                        value={content.comment}
                                                                        readOnly
                                                                    />
                                                                }
                                                            </FormStack>
                                                        </FormRow>

                                                        <FormRow style={{flex: 1}}>
                                                            <MetricInput
                                                                label='Gewicht'
                                                                value={content.weight}
                                                                append='g'
                                                                readOnly
                                                            />

                                                            {content.price ?
                                                                <CurrencyInput
                                                                    label='Prijs'
                                                                    value={formatPrice(parsePrice(content.price) + parsePrice(content.taxAmount))}
                                                                    readOnly
                                                                /> :
                                                                <CurrencyInput
                                                                    label='Prijs'
                                                                    value={content.value}
                                                                    readOnly
                                                                />
                                                            }
                                                        </FormRow>
                                                    </FormRow>
                                                </Card>
                                            )
                                        }) :
                                        parcel.customs?.content?.map((content, index) => {
                                            return (
                                                <Card key={`content${index}`} style={{background: Colors.grey20, padding: formSpacing.inset}}>
                                                    <FormRow>
                                                        <FormRow style={{flex: 1}}>
                                                            <Input
                                                                style={{maxWidth: 60}}
                                                                label='Aantal'
                                                                value={content.quantity}
                                                                readOnly
                                                            />

                                                            <FormStack style={{flex: 1}}>
                                                                <TextArea
                                                                    inputStyle={readOnlyTextStyle}
                                                                    label='Omschrijving'
                                                                    value={content.description}
                                                                    readOnly
                                                                />
                                                                {content.comment &&
                                                                    <TextArea
                                                                        inputStyle={readOnlyTextStyle}
                                                                        label='Reden'
                                                                        value={content.comment}
                                                                        readOnly
                                                                    />
                                                                }
                                                            </FormStack>
                                                        </FormRow>

                                                        <FormRow style={{flex: 1}}>
                                                            <MetricInput
                                                                label='Gewicht'
                                                                max={999999999}
                                                                divisionFactor={1000}
                                                                value={content.weight}
                                                                append='kg'
                                                                readOnly
                                                            />

                                                            <CurrencyInput
                                                                label={parcel.customs?.typeItem ? 'Waarde' : 'Prijs'}
                                                                prepend={getCurrencySymbol(getCustomsCurrency(parcel.customs))}
                                                                value={content.value}
                                                                readOnly
                                                                info='Dit is de waarde van het product voor de douane, deze kan afwijken van de prijs van het product.'
                                                            />

                                                            {parcel.customs?.typeItem &&
                                                                <>
                                                                    <Input
                                                                        label='HS-code'
                                                                        value={content.hsCode}
                                                                        readOnly
                                                                    />

                                                                    <Input
                                                                        label='Landcode'
                                                                        value={content.countryOrigin}
                                                                        readOnly
                                                                    />
                                                                </>
                                                            }
                                                        </FormRow>
                                                    </FormRow>
                                                </Card>
                                            )
                                        })}
                                </div>
                            </DividerHeader>
                        }

                        {message && <Alert style={{marginBottom: 6}} variant='success'>{message}</Alert>}
                        {error && <Alert style={{marginBottom: 6}} variant='danger'>{error}</Alert>}

                        <DividerHeader style={{marginBottom: 18}} title='Historie'>
                            <ScrollView style={{maxHeight: 250}}>
                                {[...parcel.history].reverse().map((entry, index) => {
                                    if (entry.type === 'resellerOnly' && user.isCustomer) {
                                        return
                                    }

                                    if (entry.type === 'invoice' && (user.isCustomer || (!(user as User).permissions?.invoices))) {
                                        return
                                    }

                                    return (
                                        <div key={`history${index}`} style={{display: 'flex', alignItems: 'center', padding: 5}}>
                                            <div style={{height: 15, width: 15, borderRadius: '50%', marginRight: 15, background: !entry.status || entry.status === 1 ? Colors.grey20 : Colors.parcelStatus[entry.status]}} />

                                            <div style={{flex: 1}}>
                                                {entry.type === 'invoice' ?
                                                    <Link onClick={() => history.push('/facturatie', {invoiceId: entry.invoiceId})}>{entry.description}</Link> :
                                                    <P>{entry.description || getStatusText(entry, parcel)}</P>
                                                }
                                            </div>

                                            {!user.isCustomer ?
                                                <S2 style={{marginRight: 15}}>{`${entry.who} | ${entry.eventTime || entry.timestamp ? moment(entry.eventTime || entry.timestamp).format('DD-MM-YYYY HH:mm:ss') : entry.time}`}</S2> :
                                                <S2 style={{marginRight: 15}}>{`${entry.eventTime || entry.timestamp ? moment(entry.eventTime || entry.timestamp).format('DD-MM-YYYY HH:mm:ss') : entry.time}`}</S2>
                                            }

                                        </div>
                                    )
                                })}
                            </ScrollView>

                            {['Cycloon', 'DHL', 'DHL-europlus', 'PostNL', 'Packs', 'DHL-onlineparcel', 'DPD-onlineparcel', 'PostNL-onlineparcel'].indexOf(parcel.carrier) > -1 &&
                                <IconButton loading={loadingStatus} onClick={this.onGetStatus.bind(this)}>
                                    <i className='mdi mdi-refresh' />
                                </IconButton>
                            }
                        </DividerHeader>
                    </>
                }

                {parcelError &&
                    <Alert style={{marginBottom: 6}} variant='danger'>{parcelError}</Alert>
                }

                <SendTrackTracePopup ref={(ref) => this.sendTrackTracePopup = ref} />
                {user.isCustomer &&
                    <CustomerSupportModal
                        reseller={reseller}
                        onChange={(parcel) => this.setState({parcel})}
                        ref={(ref) => this.customerSupportModal = ref}
                    />}
            </Modal>
        )
    }
}

const getStatusText = (entry, parcel) => {
    if (!entry.status) return ''

    const statusText = dbCodes.parcelStatus[entry.status]
    const isCreatedStatus = entry.status === dbCodes.parcelStatus.aangemaakt()

    if (isCreatedStatus && (parcel.plugin || parcel.webshop)) {
        const pluginOrWebshop = parcel.plugin || parcel.webshop
        const displayText = ['CSV', 'API'].includes(pluginOrWebshop) ? pluginOrWebshop : _.capitalize(pluginOrWebshop)
        return `${statusText} (${displayText})`
    }

    return statusText
}
